import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import {
    IInfiniteGetNodeContentQueryData,
    IUseInfiniteGetNodeContentQueryMeta,
} from '@/api/nodes/queries/get-node-content-query'
import { IApiResponse } from '@/utils/api/api-types'
import { QueryClient, QueryKey } from '@tanstack/react-query'

type IUpdateInfiniteNodesOptimisticallyParams = {
    qc: QueryClient
    nodeContentQueryKey: QueryKey
    onSuccess: (count: number) => unknown
    assignPrev: (prev: IInfiniteGetNodeContentQueryData) => void
} & (
    | { count: number }
    | {
          allFromDirectoryPath: string
          selectedCount: number
      }
) &
    (
        | {
              filterPageData: (
                  node: INode,
                  index: number,
                  arr: INode[],
                  meta: { nodesCount: number; meta: IUseInfiniteGetNodeContentQueryMeta | undefined }
              ) => boolean
          }
        | {
              reducePage: (
                  acc: INode[],
                  node: INode,
                  index: number,
                  arr: INode[],
                  meta: { nodesCount: number; meta: IUseInfiniteGetNodeContentQueryMeta | undefined }
              ) => INode[]
          }
    )

export const updateInfiniteNodesOptimistically = (params: IUpdateInfiniteNodesOptimisticallyParams) => {
    const nodesCount = 'count' in params ? params.count : params.selectedCount

    try {
        const meta = params.qc.getQueryCache().find({
            queryKey: params.nodeContentQueryKey,
        })?.meta as IUseInfiniteGetNodeContentQueryMeta | undefined

        params.qc.setQueryData<IInfiniteGetNodeContentQueryData>(params.nodeContentQueryKey, (prev) => {
            if (!prev) return

            params.assignPrev(prev as IInfiniteGetNodeContentQueryData)

            const updatedPages = prev.pages.reduce<IApiResponse<INode[]>[]>(
                (acc, page) => {
                    const updatedPageData =
                        'reducePage' in params
                            ? page.data.reduce<INode[]>(
                                  (acc, item, index, arr) =>
                                      params.reducePage(acc, item, index, arr, { nodesCount, meta }),
                                  [] as INode[]
                              )
                            : page.data.filter((node, index, arr) =>
                                  params.filterPageData(node, index, arr, { nodesCount, meta })
                              )

                    const nextPageResponse: IApiResponse<INode[]> = {
                        data: updatedPageData,
                        meta: page.meta,
                    }

                    return [...acc, nextPageResponse] satisfies IApiResponse<INode[]>[]
                },
                [] as IApiResponse<INode[]>[]
            )

            return {
                ...prev,
                pages: updatedPages,
            }
        })

        params.onSuccess(nodesCount)
    } catch (error) {
        console.error('Failed to update node content query data', error)
    }
}
